import { api } from "../api/client.js";
import { env } from "../core/env.js";
import { readCookie, writeCookie } from "@bitcashier/shared/core/cookies.js";
import { convertFromScientificFormat } from "../core/formatters.js";
import { viewingAsUserId } from "../viewAsStore.js";

// Port of src/user/context/WebSocketContext.tsx — the live price + balance feed behind the trading
// screen, the balances screen and the header read-out.
//
// Protocol (unchanged): GET /ui/ws-token/ issues a short-lived JWT, the socket connects to
// `${websocketAddress}/v1//ws?token=…` (yes, the double slash is what the gateway expects), then we
// send one `subscribe_prices` per displayed pair plus a single `subscribe_balances`. Inbound frames
// are `price`, `balances` or `error`.

const DEFAULT_PAIRS = {
  0: "BTC/EUR",
  1: "BTC/USD",
  2: "BTC/GBP",
  3: "USDT/EUR",
  4: "USDC/USD",
  5: "USDC/USDT",
};

const PAIRS_COOKIE = "saved-pairs";
const TX_FEE_COOKIE = "includeTxFee";

// React had no reconnect at all: a dropped socket left prices frozen until the page was reloaded.
// We retry with a capped backoff, re-issuing the token each time since it is short-lived.
const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

export const marketData = $state({
  connected: false,
  // Latest price frame per pair tag, e.g. { "BTC/EUR": { ask, bid, primaryAsset, secondaryAsset } }.
  prices: {},
  // Latest balance snapshot: [{ asset, balance, available }].
  balances: [],
  balancesLoaded: false,
  // Displayed pairs, keyed by slot index exactly like the `saved-pairs` cookie.
  pairs: readPairs(),
  includeTxFee: readCookie(TX_FEE_COOKIE) === "true",
});

function readPairs() {
  const raw = readCookie(PAIRS_COOKIE);
  if (!raw) {
    writeCookie(PAIRS_COOKIE, JSON.stringify(DEFAULT_PAIRS));
    return { ...DEFAULT_PAIRS };
  }
  try {
    return JSON.parse(decodeURIComponent(raw));
  } catch {
    return { ...DEFAULT_PAIRS };
  }
}

function persistPairs(pairs) {
  marketData.pairs = pairs;
  writeCookie(PAIRS_COOKIE, JSON.stringify(pairs));
}

let socket = null;
let attempt = 0;
let closedByUs = false;

// The token is a JWT; we only need its `exp` to avoid opening a socket that will be rejected.
function isExpired(token) {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return !(payload?.exp > Math.floor(Date.now() / 1000));
  } catch {
    return true;
  }
}

export async function connect() {
  // While a Super admin views the dashboard as someone else the backend refuses the token, so there
  // is no socket to open — and retrying one would hammer that refusal forever. Balances are shown
  // from the REST snapshot instead (showSnapshotBalances); prices simply do not stream.
  if (viewingAsUserId()) return;
  closedByUs = false;
  let token;
  try {
    const data = await api.get("/ui/ws-token/");
    token = data?.token;
  } catch {
    return scheduleReconnect();
  }
  if (!token || isExpired(token)) return scheduleReconnect();

  socket = new WebSocket(`${env.websocketAddress}/v1//ws?token=${token}`);

  socket.onopen = () => {
    attempt = 0;
    marketData.connected = true;
    Object.values(marketData.pairs).forEach((pair) => subscribePair(pair));
    send({ type: "subscribe_balances" });
  };

  socket.onmessage = (event) => handleMessage(JSON.parse(event.data));

  socket.onclose = () => {
    marketData.connected = false;
    if (!closedByUs) scheduleReconnect();
  };

  // onerror is always followed by onclose, so reconnecting is handled there.
  socket.onerror = () => {};
}

function scheduleReconnect() {
  const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];
  attempt += 1;
  setTimeout(() => {
    if (!closedByUs) connect();
  }, delay);
}

export function disconnect() {
  closedByUs = true;
  socket?.close();
  socket = null;
  marketData.connected = false;
}

// Stands in for the socket's balances frame where there is no socket (see connect): the GET
// /ui/balances snapshot's `available` per asset is the same amount the frame would have carried.
export function showSnapshotBalances(snapshot) {
  marketData.balances = (snapshot?.balances ?? []).map((entry) => ({
    asset: entry.asset,
    balance: convertFromScientificFormat(entry.available),
    available: entry.available,
  }));
  marketData.balancesLoaded = true;
}

// Every outbound frame carries the current includeTxFee, matching React's sendData wrapper.
export function send(message) {
  if (socket?.readyState !== WebSocket.OPEN) return false;
  const payload = message.payload ? { ...message.payload, includeTxFee: marketData.includeTxFee } : undefined;
  socket.send(JSON.stringify(payload ? { ...message, payload } : message));
  return true;
}

export function subscribePair(pair, primaryAmount = 1) {
  const [primaryAsset, secondaryAsset] = pair.split("/");
  return send({
    type: "subscribe_prices",
    payload: { primaryAsset, secondaryAsset, primaryAmount, tag: pair },
  });
}

export function unsubscribePair(pair) {
  const [primaryAsset, secondaryAsset] = pair.split("/");
  return send({
    type: "unsubscribe_prices",
    payload: { primaryAsset, secondaryAsset, tag: pair },
  });
}

// Toggling the fee basis changes what the server quotes, so React tore the socket down and
// re-subscribed from scratch. Same here, minus the full token/state reset.
export function setIncludeTxFee(value) {
  marketData.includeTxFee = value;
  writeCookie(TX_FEE_COOKIE, String(value));
  marketData.prices = {};
  disconnect();
  connect();
}

export function removePair(pair) {
  const remaining = Object.values(marketData.pairs).filter((value) => value !== pair);
  // Re-key so slots stay contiguous — the cookie is an index-keyed object, not an array.
  persistPairs(Object.fromEntries(remaining.map((value, index) => [index, value])));
  unsubscribePair(pair);
}

export function setPair(slot, pair) {
  persistPairs({ ...marketData.pairs, [slot]: pair });
  subscribePair(pair);
}

export function addPair(pair) {
  persistPairs({ ...marketData.pairs, [Object.keys(marketData.pairs).length]: pair });
  subscribePair(pair);
}

function handleMessage(response) {
  switch (response.type) {
    case "price":
      marketData.prices = { ...marketData.prices, [response.payload.tag]: response.payload };
      return;
    case "balances":
      marketData.balances = response.payload.balances.map((entry) => ({
        ...entry,
        balance: convertFromScientificFormat(entry.balance),
      }));
      marketData.balancesLoaded = true;
      return;
    case "error":
      handleError(response.payload);
      return;
    default:
      return;
  }
}

// The one error the server sends that we act on: a pair it cannot quote is dropped from the
// display (and the cookie) so it stops being re-subscribed on every reconnect.
function handleError({ error }) {
  if (typeof error === "string" && error.startsWith("Unsupported asset pair")) {
    removePair(error.split(":")[1]?.trim());
  }
}
